import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { Plus, Briefcase, CheckCircle, XCircle, Clock, Pencil, Trash, UsersThree } from "@phosphor-icons/react";

const STATUS_META = {
  pending: { label: "Pending", color: "#fbbf24", icon: Clock },
  paid: { label: "Paid", color: "#22c55e", icon: CheckCircle },
  lost: { label: "Lost", color: "#ef4444", icon: XCircle },
};

const CASE_TYPES = [
  { key: "life", label: "Life", color: "#0ea5e9", depositLabel: "Face Amount $", depositPlaceholder: "250000" },
  { key: "annuity", label: "Annuity", color: "#a855f7", depositLabel: "Annuity Deposit $", depositPlaceholder: "100000" },
  { key: "investment", label: "Investment", color: "#f97316", depositLabel: "Investment Deposit $", depositPlaceholder: "50000" },
];
const CASE_TYPE_META = Object.fromEntries(CASE_TYPES.map((t) => [t.key, t]));

const FILTERS = ["all", "pending", "paid", "lost"];

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const emptyForm = {
  client_name: "",
  carrier_id: "",
  product: "",
  case_type: "life",
  target_premium: "",
  annuity_deposit: "",
  status: "pending",
  stage: "Submitted",
  notes: "",
  is_joint: false,
  joint_mode: "cfn", // cfn | external
  joint_rep_user_id: "",
  joint_rep_name: "",
  joint_split_pct: 50,
};

export default function Cases() {
  const { user } = useAuth();
  const isMpOrAdmin = user?.role === "managing_partner" || user?.role === "admin";
  const [filter, setFilter] = useState("all");
  const [scope, setScope] = useState(user?.role === "managing_partner" ? "team" : "mine");
  const [agentFilter, setAgentFilter] = useState(""); // empty = all
  const [groupByAgent, setGroupByAgent] = useState(false);
  const [cases, setCases] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [users, setUsers] = useState([]);
  const [stages, setStages] = useState({ pending: [], paid: [], lost: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filter !== "all") params.set("status", filter);
    if (isMpOrAdmin && scope) params.set("scope", scope);
    const { data } = await api.get(`/cases?${params.toString()}`);
    setCases(data);
  }, [filter, scope, isMpOrAdmin]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.all([
      api.get("/carriers"),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/cases/stages"),
    ]).then(([c, u, s]) => {
      setCarriers(c.data || []);
      setUsers((u.data || []).filter((x) => x.id !== user.id));
      setStages(s.data || { pending: [], paid: [], lost: [] });
    });
  }, [user.id]);

  const validStages = stages[form.status] || [];

  const stats = useMemo(() => {
    const s = { pending: 0, paid: 0, lost: 0, target_pending: 0, target_paid: 0, annuity_paid: 0, investment_paid: 0 };
    for (const c of cases) {
      s[c.status] += 1;
      if (c.status === "pending") s.target_pending += Number(c.target_premium || 0);
      if (c.status === "paid") {
        s.target_paid += Number(c.target_premium || 0);
        if (c.case_type === "investment") {
          s.investment_paid += Number(c.annuity_deposit || 0);
        } else {
          s.annuity_paid += Number(c.annuity_deposit || 0);
        }
      }
    }
    return s;
  }, [cases]);

  // Distinct agent options for the agent filter — only when more than one agent is in the list.
  const agentOptions = useMemo(() => {
    const m = new Map();
    cases.forEach((c) => { if (c.agent_id) m.set(c.agent_id, c.agent_name || "—"); });
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [cases]);

  // Apply the agent filter (server already filtered by scope + status).
  const visibleCases = useMemo(() => {
    if (!agentFilter) return cases;
    return cases.filter((c) => c.agent_id === agentFilter);
  }, [cases, agentFilter]);

  // Bucket cases by agent for the "Group by Agent" view.
  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const c of visibleCases) {
      const key = c.agent_id || "_unknown";
      const name = c.agent_name || "—";
      if (!buckets.has(key)) buckets.set(key, { name, rows: [] });
      buckets.get(key).rows.push(c);
    }
    return Array.from(buckets, ([id, v]) => ({ id, name: v.name, rows: v.rows }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [visibleCases]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      client_name: c.client_name || "",
      carrier_id: c.carrier_id || "",
      product: c.product || "",
      case_type: c.case_type || "life",
      target_premium: c.target_premium ? String(c.target_premium) : "",
      annuity_deposit: c.annuity_deposit ? String(c.annuity_deposit) : "",
      status: c.status || "pending",
      stage: c.stage || "Submitted",
      notes: c.notes || "",
      is_joint: !!c.joint?.is_joint,
      joint_mode: c.joint?.rep_user_id ? "cfn" : "external",
      joint_rep_user_id: c.joint?.rep_user_id || "",
      joint_rep_name: c.joint?.rep_name || "",
      joint_split_pct: c.joint?.split_pct ?? 50,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim()) { toast.error("Client name is required"); return; }
    const payload = {
      client_name: form.client_name.trim(),
      carrier_id: form.carrier_id || null,
      product: form.product || null,
      case_type: form.case_type,
      target_premium: Number(form.target_premium) || 0,
      annuity_deposit: Number(form.annuity_deposit) || 0,
      status: form.status,
      stage: form.stage,
      notes: form.notes || null,
      joint: form.is_joint ? {
        is_joint: true,
        rep_user_id: form.joint_mode === "cfn" ? (form.joint_rep_user_id || null) : null,
        rep_name: form.joint_mode === "external" ? (form.joint_rep_name || null) : null,
        split_pct: Math.max(0, Math.min(100, Number(form.joint_split_pct) || 0)),
      } : { is_joint: false, split_pct: 100 },
    };
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/cases/${editing.id}`, payload);
        toast.success("Case updated");
      } else {
        const { data } = await api.post("/cases", payload);
        if (data.status === "paid") {
          toast.success("Case logged + points credited!");
        } else {
          toast.success("Case logged");
        }
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete case for ${c.client_name}? This also removes any credit it earned.`)) return;
    try {
      await api.delete(`/cases/${c.id}`);
      toast.success("Case deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  // When status changes, snap stage to a valid value
  const setStatus = (newStatus) => {
    const validList = stages[newStatus] || [];
    setForm((f) => ({ ...f, status: newStatus, stage: validList[0] || "" }));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">Pipeline</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-1 flex items-center gap-3">
            Cases <Briefcase weight="fill" className="text-[#2563eb]" />
          </h1>
          <p className="text-zinc-600 mt-2">Track every case from submitted to paid. Paid cases auto-credit your points and Leader's Conference progress.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isMpOrAdmin && (
            <div className="brut bg-white p-1.5 inline-flex" data-testid="cases-scope-toggle">
              <button onClick={() => setScope("mine")} className={`px-3 py-2 font-bold uppercase text-xs tracking-widest ${scope === "mine" ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`} data-testid="cases-scope-mine">Mine</button>
              {user?.role === "managing_partner" && <button onClick={() => setScope("team")} className={`px-3 py-2 font-bold uppercase text-xs tracking-widest ${scope === "team" ? "bg-[#2563eb] text-white" : "text-black hover:bg-zinc-100"}`} data-testid="cases-scope-team">Team</button>}
              <button onClick={() => setScope("firm")} className={`px-3 py-2 font-bold uppercase text-xs tracking-widest ${scope === "firm" ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`} data-testid="cases-scope-firm">{user?.role === "admin" ? "Firm" : "All"}</button>
            </div>
          )}
          <Button onClick={openCreate} className="rounded-none bg-[#2563eb] text-white border-2 border-black h-11 px-5 shadow-[4px_4px_0_0_#09090b] hover:bg-[#1d4ed8] font-bold uppercase tracking-widest text-xs" data-testid="new-case-btn">
            <Plus size={14} weight="bold" className="mr-1" /> New Case
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="brut p-4 bg-white" data-testid="stat-pending">
          <div className="text-xs font-bold uppercase tracking-widest text-zinc-500">Pending</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1">{stats.pending}</div>
          <div className="text-xs text-zinc-500 mt-1">{fmt$(stats.target_pending)} target</div>
        </div>
        <div className="brut p-4 bg-white" data-testid="stat-paid">
          <div className="text-xs font-bold uppercase tracking-widest text-zinc-500">Paid</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1 text-[#22c55e]">{stats.paid}</div>
          <div className="text-xs text-zinc-500 mt-1">{fmt$(stats.target_paid)} target</div>
        </div>
        <div className="brut p-4 bg-white" data-testid="stat-lost">
          <div className="text-xs font-bold uppercase tracking-widest text-zinc-500">Lost</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1 text-[#ef4444]">{stats.lost}</div>
        </div>
        <div className="brut p-4 bg-white" data-testid="stat-annuity-paid">
          <div className="text-xs font-bold uppercase tracking-widest text-zinc-500">Annuity Deposits (paid)</div>
          <div className="font-display font-black text-2xl tracking-tighter mt-1 text-[#a855f7]">{fmt$(stats.annuity_paid)}</div>
        </div>
        <div className="brut p-4 bg-white" data-testid="stat-investment-paid">
          <div className="text-xs font-bold uppercase tracking-widest text-zinc-500">Investment Deposits (paid)</div>
          <div className="font-display font-black text-2xl tracking-tighter mt-1 text-[#f97316]">{fmt$(stats.investment_paid)}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="brut bg-white p-1.5 inline-flex" data-testid="cases-filter">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 font-bold uppercase text-xs tracking-widest ${filter === f ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`}
              data-testid={`filter-${f}`}
            >
              {f === "all" ? "All" : STATUS_META[f].label}
            </button>
          ))}
        </div>

        {agentOptions.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Agent</span>
            <select
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              className="border-2 border-black h-10 px-3 font-medium bg-white focus:outline-none min-w-[180px]"
              data-testid="cases-agent-filter"
            >
              <option value="">All Agents</option>
              {agentOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        )}

        {agentOptions.length > 1 && (
          <button
            onClick={() => setGroupByAgent((g) => !g)}
            className={`brut h-10 px-4 inline-flex items-center gap-2 font-bold uppercase text-xs tracking-widest border-2 border-black ${groupByAgent ? "bg-[#2563eb] text-white" : "bg-white text-black hover:bg-zinc-100"}`}
            data-testid="cases-group-toggle"
          >
            <UsersThree size={14} weight="bold" /> {groupByAgent ? "Grouped" : "Group by Agent"}
          </button>
        )}
      </div>

      {/* Table */}
      {(() => {
        const COLS = 10; // includes Agent column
        const renderRow = (c, i) => {
          const M = STATUS_META[c.status] || STATUS_META.pending;
          const T = CASE_TYPE_META[c.case_type] || CASE_TYPE_META.life;
          return (
            <motion.tr key={c.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }} className="border-b-2 border-black last:border-b-0 hover:bg-[#f4f4f5]" data-testid={`case-row-${c.id}`}>
              <td className="px-3 py-2 font-bold">{c.client_name}</td>
              <td className="px-3 py-2 text-zinc-700 font-medium" data-testid={`case-agent-${c.id}`}>{c.agent_name || <span className="text-zinc-400">—</span>}</td>
              <td className="px-3 py-2">
                <span className="inline-flex items-center px-2 py-0.5 border-2 border-black text-[10px] font-black uppercase tracking-widest text-white" style={{ background: T.color }} data-testid={`case-type-${c.id}`}>{T.label}</span>
              </td>
              <td className="px-3 py-2 text-zinc-700">
                {c.carrier_name && <span>{c.carrier_name}</span>}
                {c.carrier_name && c.product && <span className="text-zinc-400"> · </span>}
                {c.product && <span>{c.product}</span>}
                {!c.carrier_name && !c.product && <span className="text-zinc-400">—</span>}
              </td>
              <td className="px-3 py-2 text-right">{c.target_premium ? fmt$(c.target_premium) : <span className="text-zinc-400">—</span>}</td>
              <td className="px-3 py-2 text-right">{c.annuity_deposit ? fmt$(c.annuity_deposit) : <span className="text-zinc-400">—</span>}</td>
              <td className="px-3 py-2 text-zinc-700">
                {c.joint?.is_joint ? (
                  <span className="inline-flex items-center gap-1 text-xs">
                    <UsersThree size={14} weight="fill" />
                    {c.joint.rep_user_name || c.joint.rep_name || "—"}
                    <span className="text-zinc-400 ml-1">{c.joint.split_pct}%</span>
                  </span>
                ) : <span className="text-zinc-400">Solo</span>}
              </td>
              <td className="px-3 py-2 text-xs">{c.stage}</td>
              <td className="px-3 py-2">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 border-2 border-black text-[10px] font-black uppercase tracking-widest" style={{ background: M.color, color: c.status === "paid" || c.status === "lost" ? "#fff" : "#09090b" }}>
                  <M.icon size={10} weight="bold" /> {M.label}
                </span>
              </td>
              <td className="px-3 py-2 text-right">
                <div className="inline-flex gap-2">
                  <button onClick={() => openEdit(c)} className="border-2 border-black p-1.5 hover:bg-[#fde68a]" data-testid={`edit-case-${c.id}`}>
                    <Pencil size={12} weight="bold" />
                  </button>
                  <button onClick={() => del(c)} className="border-2 border-black p-1.5 hover:bg-[#fb7185] hover:text-white" data-testid={`delete-case-${c.id}`}>
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
              </td>
            </motion.tr>
          );
        };

        const headerRow = (
          <tr className="bg-[#09090b] text-white">
            <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Client</th>
            <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Agent</th>
            <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Type</th>
            <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Carrier · Product</th>
            <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Target $</th>
            <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Deposit $</th>
            <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Joint</th>
            <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Stage</th>
            <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Status</th>
            <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Actions</th>
          </tr>
        );

        return (
          <div className="brut bg-white overflow-x-auto">
            <table className="w-full border-collapse" data-testid="cases-table">
              <thead>{headerRow}</thead>
              <tbody>
                {!groupByAgent && visibleCases.map((c, i) => renderRow(c, i))}
                {groupByAgent && grouped.map((g) => (
                  <React.Fragment key={g.id}>
                    <tr className="bg-[#fde68a] border-y-2 border-black" data-testid={`agent-group-${g.id}`}>
                      <td colSpan={COLS} className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div className="font-display font-black uppercase tracking-tight text-sm flex items-center gap-2">
                            <UsersThree size={16} weight="fill" /> {g.name}
                          </div>
                          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-700">
                            {g.rows.length} case{g.rows.length === 1 ? "" : "s"}
                            {" · "}
                            paid {fmt$(g.rows.filter((r) => r.status === "paid").reduce((a, r) => a + Number(r.target_premium || 0), 0))}
                          </div>
                        </div>
                      </td>
                    </tr>
                    {g.rows.map((c, i) => renderRow(c, i))}
                  </React.Fragment>
                ))}
                {visibleCases.length === 0 && (
                  <tr><td colSpan={COLS} className="px-4 py-10 text-center text-zinc-500">No cases yet. Click &ldquo;New Case&rdquo; to log one.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Create / Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#09090b] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display font-black tracking-tight">{editing ? "Edit Case" : "New Case"}</DialogTitle>
            <DialogDescription className="text-zinc-600">
              Log a case with carrier, premium, status, and joint-rep split. Cases marked Paid auto-credit your points and Leader's Conference progress.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3" data-testid="case-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="font-bold uppercase tracking-widest text-xs">Client Name *</Label>
                <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required placeholder="John Smith" className="mt-1 border-2 border-black rounded-none h-10 focus-visible:ring-0" data-testid="client-name-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="font-bold uppercase tracking-widest text-xs">Case Type *</Label>
                <div className="mt-1 inline-flex border-2 border-black bg-white p-1" data-testid="case-type-toggle">
                  {CASE_TYPES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setForm({ ...form, case_type: t.key })}
                      className={`px-3 py-1.5 font-bold uppercase text-xs tracking-widest transition-colors ${form.case_type === t.key ? "text-white" : "text-black hover:bg-zinc-100"}`}
                      style={form.case_type === t.key ? { background: t.color } : {}}
                      data-testid={`case-type-${t.key}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="font-bold uppercase tracking-widest text-xs">Carrier</Label>
                <select value={form.carrier_id} onChange={(e) => setForm({ ...form, carrier_id: e.target.value })} className="mt-1 w-full h-10 border-2 border-black px-3 font-medium bg-white" data-testid="carrier-select">
                  <option value="">—</option>
                  {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="font-bold uppercase tracking-widest text-xs">Product</Label>
                <Input value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder={form.case_type === "investment" ? "Brokerage, IRA, Roth, Mutual Fund…" : form.case_type === "annuity" ? "FIA, MYGA, VA…" : "IUL, Term, Whole, LTC…"} className="mt-1 border-2 border-black rounded-none h-10 focus-visible:ring-0" data-testid="product-input" />
              </div>
              <div>
                <Label className="font-bold uppercase tracking-widest text-xs">Target Premium $</Label>
                <Input type="number" min="0" step="0.01" value={form.target_premium} onChange={(e) => setForm({ ...form, target_premium: e.target.value })} placeholder="5000" className="mt-1 border-2 border-black rounded-none h-10 focus-visible:ring-0" data-testid="target-premium-input" />
              </div>
              <div>
                <Label className="font-bold uppercase tracking-widest text-xs">{CASE_TYPE_META[form.case_type]?.depositLabel || "Deposit $"}</Label>
                <Input type="number" min="0" step="0.01" value={form.annuity_deposit} onChange={(e) => setForm({ ...form, annuity_deposit: e.target.value })} placeholder={CASE_TYPE_META[form.case_type]?.depositPlaceholder || "100000"} className="mt-1 border-2 border-black rounded-none h-10 focus-visible:ring-0" data-testid="annuity-deposit-input" />
              </div>
              <div>
                <Label className="font-bold uppercase tracking-widest text-xs">Status</Label>
                <select value={form.status} onChange={(e) => setStatus(e.target.value)} className="mt-1 w-full h-10 border-2 border-black px-3 font-medium bg-white" data-testid="status-select">
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
              <div>
                <Label className="font-bold uppercase tracking-widest text-xs">Stage</Label>
                <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="mt-1 w-full h-10 border-2 border-black px-3 font-medium bg-white" data-testid="stage-select">
                  {validStages.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {/* Joint section */}
            <div className="border-2 border-black p-3 bg-zinc-50">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_joint} onChange={(e) => setForm({ ...form, is_joint: e.target.checked })} className="w-4 h-4 accent-[#2563eb]" data-testid="joint-toggle" />
                <span className="font-bold uppercase tracking-widest text-xs">Sold jointly with another rep</span>
              </label>
              {form.is_joint && (
                <div className="mt-3 space-y-3">
                  <div className="inline-flex border-2 border-black p-1 bg-white" data-testid="joint-mode-toggle">
                    <button type="button" onClick={() => setForm({ ...form, joint_mode: "cfn" })} className={`px-3 py-1.5 font-bold uppercase text-xs tracking-widest ${form.joint_mode === "cfn" ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`} data-testid="joint-cfn">CFN Rep</button>
                    <button type="button" onClick={() => setForm({ ...form, joint_mode: "external" })} className={`px-3 py-1.5 font-bold uppercase text-xs tracking-widest ${form.joint_mode === "external" ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`} data-testid="joint-external">External</button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {form.joint_mode === "cfn" ? (
                      <div>
                        <Label className="font-bold uppercase tracking-widest text-xs">CFN Rep</Label>
                        <select value={form.joint_rep_user_id} onChange={(e) => setForm({ ...form, joint_rep_user_id: e.target.value })} className="mt-1 w-full h-10 border-2 border-black px-3 font-medium bg-white" data-testid="joint-rep-user-select">
                          <option value="">— select —</option>
                          {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                        </select>
                      </div>
                    ) : (
                      <div>
                        <Label className="font-bold uppercase tracking-widest text-xs">Rep Name (External)</Label>
                        <Input value={form.joint_rep_name} onChange={(e) => setForm({ ...form, joint_rep_name: e.target.value })} placeholder="Jane Doe" className="mt-1 border-2 border-black rounded-none h-10 focus-visible:ring-0" data-testid="joint-rep-name-input" />
                      </div>
                    )}
                    <div>
                      <Label className="font-bold uppercase tracking-widest text-xs">Your Split %</Label>
                      <Input type="number" min="0" max="100" step="1" value={form.joint_split_pct} onChange={(e) => setForm({ ...form, joint_split_pct: e.target.value })} className="mt-1 border-2 border-black rounded-none h-10 focus-visible:ring-0" data-testid="split-pct-input" />
                      <div className="text-[10px] text-zinc-500 mt-1">Other rep receives {Math.max(0, 100 - (Number(form.joint_split_pct) || 0))}% credit.</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="font-bold uppercase tracking-widest text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 border-2 border-black rounded-none focus-visible:ring-0" data-testid="notes-input" />
            </div>

            <Button type="submit" disabled={saving} className="w-full rounded-none bg-[#09090b] text-white border-2 border-black h-11 font-bold uppercase tracking-widest text-xs" data-testid="save-case-btn">
              {saving ? "Saving…" : (editing ? "Save Changes" : "Log Case")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
